<template>
    <page :busy="busy" :tabs="tabs">
        <validation-observer ref="form" class="toolbar-wrapper" v-slot="{ errors, setFieldValue, meta }">
            <b-row class="p-2 toolbar-row no-gutters flex-md-row flex-column">
                <b-col md="3">
                    <validation-provider class="w-100" name="title" rules="required" as="div">
                        <form-input
                            @input="onChangeTitle"
                            :error="errors.title"
                            v-model="title"
                            class="mr-2 mb-0"
                            label="Form Name:"
                            :disabled="isReadOnly"
                        ></form-input>
                    </validation-provider>
                </b-col>

                <b-col md="4">
                    <div class="d-flex align-items-baseline">
                        <validation-provider class="w-100" name="type" rules="required" as="div">
                            <form-select
                                :error="errors['type']"
                                v-model="type"
                                label="Type: "
                                :options="formTypeOptions"
                                :disabled="!!formId"
                                class="mb-0"
                                @change="(val) => setFieldValue('type', val)"
                            ></form-select>
                        </validation-provider>
                        <more-filters-dropdown
                            v-slot:default="{ handleDropdownShow, handleDropdownHide }"
                            class="more-filters ml-2"
                        >
                            <div class="dropdown-content">
                                <validation-provider
                                    v-if="type !== 'HOUSEKEEPER'"
                                    class="w-100"
                                    name="variable.name"
                                    rules="required"
                                    as="div"
                                >
                                    <form-input
                                        :error="errors['variable.name']"
                                        v-model="customVariable"
                                        label="Merge Tag:"
                                        description="Merge tags are used to provide a link to the form in guest communications."
                                        :disabled="isReadOnly"
                                        @input="(val) => setFieldValue('variable.name', val)"
                                    ></form-input>
                                </validation-provider>
                                <validation-provider
                                    v-if="type !== 'HOUSEKEEPER'"
                                    class="w-100"
                                    name="variable.title"
                                    rules="required"
                                    as="div"
                                >
                                    <form-input
                                        :error="errors['variable.title']"
                                        v-model="customVariableName"
                                        label="Merge Tag Description:"
                                        description="Used to identify merge tag purpose in guest communications."
                                        :disabled="isReadOnly"
                                        @input="(val) => setFieldValue('variable.title', val)"
                                    ></form-input>
                                </validation-provider>
                                <label
                                    v-if="type === 'INVOICE_VARIABLES'"
                                    class="d-flex align-items-end justify-content-between"
                                    >Displayed to guests at time of booking?
                                    <form-switch class="ml-1" v-model="show_on_booking"
                                /></label>
                                <validation-provider
                                    v-if="type !== 'INVOICE_VARIABLES'"
                                    class="w-100"
                                    name="properties"
                                    as="div"
                                >
                                    <properties-search-multiselect
                                        class="property-select"
                                        v-model="selectedProperties"
                                        :is-form="true"
                                        :properties="properties"
                                        :error="errors[0]"
                                        label="Properties"
                                        :controlValue="controlValue"
                                        placeholder="Send to all properties"
                                        @show="handleDropdownShow"
                                        @hide="handleDropdownHide"
                                    ></properties-search-multiselect>
                                </validation-provider>
                                <label v-if="type === 'HOUSEKEEPER'"
                                    >Enabled <form-switch class="ml-1" v-model="enabled"
                                /></label>
                            </div>
                        </more-filters-dropdown>
                    </div>
                </b-col>
                <b-col class="d-flex align-items-end">
                    <b-button @click="saveForm" :disabled="!meta.valid" class="ml-md-2" variant="primary">
                        Save Form
                    </b-button>
                </b-col>
            </b-row>
        </validation-observer>
        <form-builder
            v-if="type"
            :type="type"
            :initialData="formData"
            @busy="handleBusy"
            @changeData="handleChangeData"
            :readOnly="isReadOnly"
        />
        <!-- <modal :id="confirmationModalId" title="Confirm Changes" @ok="confirmChanges" ok-title="Confirm">
            Changes made to this form are saved automatically. When creating a form, a form type must be selected. This
            selection is not editable. Please take a moment to review your form type selection before proceeding.
        </modal> -->
    </page>
</template>

<script>
    import page from '../generic/page/page.vue';

    import FormInput from '../generic/form-input/form-input.vue';
    import { formatResponseError } from '../../utils/api';
    import { HOSTED_FORM_ENDPOINT } from '../../endpoints';
    import api from '../../api';
    import formSquareSwitch from '../generic/form-square-switch/form-square-switch.vue';
    import formSelect from '../generic/form-select/form-select.vue';
    import propertiesSearchMultiselect from '../generic/properties-search-multiselect/properties-search-multiselect.vue';
    import moreFiltersDropdown from '../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import FormSwitch from '../generic/form-switch/form-switch.vue';
    import Modal from '../generic/modal/modal.vue';
    import { removeScrollMixin } from '../../mixins/remove-scroll-mixin';
    import { defineAsyncComponent } from 'vue';

    const FormBuilder = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-form-builder" */
            './form-builder.vue'
        )
    );
    export const hostedFormTypeOptions = [
        {
            value: 'GUEST',
            text: 'Guest Form',
        },
        {
            value: 'HOUSEKEEPER',
            text: 'Housekeeper Checklist',
        },
        {
            value: 'GUEST_REVIEW',
            text: 'Guest Review',
        },
        {
            value: 'INVOICE_VARIABLES',
            text: 'Invoice Variables ',
        },
    ];

    export default {
        name: 'survey-creator',
        mixins: [removeScrollMixin],
        components: {
            FormBuilder,
            page,
            FormInput,
            formSquareSwitch,
            formSelect,
            propertiesSearchMultiselect,
            moreFiltersDropdown,
            FormSwitch,
            Modal,
        },
        data() {
            return {
                formData: null,
                formBuilder: null,
                busy: false,
                tabs: [
                    {
                        title: 'Hosted Form List',
                        route: '/app/hosted-form/list/',
                        active: false,
                    },
                    {
                        title: 'Form Builder',
                        active: true,
                    },
                    {
                        title: 'Requested Forms',
                        route: '/app/hosted-form/pending/',
                        active: false,
                    },
                    {
                        title: 'Сompleted Forms',
                        route: '/app/hosted-form/completed/',
                        active: false,
                    },
                ],
                title: '',
                customVariable: '',
                customVariableName: '',
                type: '',
                selectedProperties: [],
                properties: [],
                isLimited: false,
                enabled: false,
                confirmationModalId: 'confirmation-modal',
                // isChangesConfirmed: false,
                show_on_booking: false,
                toastCount: 0,
            };
        },
        computed: {
            formId() {
                return Number(this.$route.params.id);
            },
            formTypeOptions() {
                const options = [...hostedFormTypeOptions];
                if (this.type !== 'GUEST_REVIEW') {
                    const index = options.findIndex((item) => item.value === 'GUEST_REVIEW');
                    options.splice(index, 1);
                }
                if (this.type !== 'INVOICE_VARIABLES') {
                    const index = options.findIndex((item) => item.value === 'INVOICE_VARIABLES');
                    options.splice(index, 1);
                }
                return options;
            },
            isReadOnly() {
                return this.isLimited && this.type === 'GUEST_REVIEW';
            },
            controlValue() {
                return this.selectedProperties.length > 0 ? 'Properties' : 'All properties';
            },
        },
        mounted() {
            this.fetchData();
        },
        methods: {
            confirmChanges() {
                this.isChangesConfirmed = true;
                this.$bvModal.hide(this.confirmationModalId);
                this.saveForm(true, true);
            },
            handleBusy(busy) {
                this.busy = busy;
            },
            handleChangeData(data) {
                this.formBuilder = data;
                this.formData = {
                    ...data.JSON,
                    elements: data.JSON.elements
                        ? data.JSON.elements.map((item) => {
                              if (item.type === 'rating' && item.rateType === 'stars') {
                                  return {
                                      ...item,
                                      displayMode: 'buttons',
                                  };
                              }
                              return item;
                          })
                        : [],
                };
            },
            onChangeTitle(value) {
                if (!this.isReadOnly) {
                    this.customVariable = value.split(' ').join('_').toUpperCase();
                    this.customVariableName = value;
                    this.$refs.form.setFieldValue('title', value);
                    this.$refs.form.setFieldValue('variable.name', value.split(' ').join('_').toUpperCase());
                    this.$refs.form.setFieldValue('variable.title', value);
                }
            },

            async fetchProperties() {
                try {
                    const { data } = await api.get(HOSTED_FORM_ENDPOINT() + 'unassigned-properties/');
                    const allProperties = data.all_properties;
                    const unassignedProperties = data.unassigned_properties;
                    this.properties = [
                        ...allProperties.filter((item) =>
                            this.selectedProperties.map((item) => item.property_id).includes(item.property_id)
                        ),
                        ...unassignedProperties,
                    ];
                } catch (e) {
                    this.errorToast({ text: 'Could not load properties' });
                }
            },
            async fetchData() {
                if (this.formId) {
                    try {
                        this.handleBusy(true);
                        const { data } = await api.get(HOSTED_FORM_ENDPOINT(this.formId));
                        this.type = data.type;
                        this.formData = data.form_json;
                        this.title = data.title;
                        this.customVariable = data.variable?.name || '';
                        this.customVariableName = data.variable?.title || '';
                        this.selectedProperties = data.properties.map((item) => ({
                            property_id: item.property_id,
                        }));
                        await this.fetchProperties();
                        this.isLimited = data.is_limited;
                        this.enabled = data.is_enabled;
                        this.isChangesConfirmed = true;
                        this.show_on_booking = data.show_on_booking;
                        this.$refs.form.setValues({
                            title: data.title,
                            type: data.type,
                            variable: data.variable,
                        });
                    } catch (e) {
                        const { generic } = formatResponseError(e);
                        this.errorToast({ text: generic || 'Could not load form' });
                    } finally {
                        this.handleBusy(false);
                    }
                } else {
                    this.fetchProperties();
                    this.formData = {};
                    this.type = this.formTypeOptions[0].value;
                    this.$refs.form.setValues({
                        title: '',
                        type: this.formTypeOptions[0].value,
                        'variable.name': '',
                        'variable.title': '',
                    });
                }
            },

            async saveForm(showLoading = true, showSuccess = true) {
                try {
                    if (showLoading) {
                        this.handleBusy(true);
                    }
                    await new Promise((resolve) => {
                        setTimeout(() => {
                            resolve(true);
                        }, 500);
                    });
                    if (!this.title || !this.formData.elements || this.formData.elements.length === 0) {
                        return;
                    }
                    const notAllMergeTagsAreFilled = this.formData.elements.filter((item) => !item.mergeTag);

                    if (this.type === 'INVOICE_VARIABLES' && notAllMergeTagsAreFilled.length) {
                        const questions = notAllMergeTagsAreFilled.map((item) => item.title || item.name).join(', ');
                        this.errorToast({
                            text: `Please fill merge tag to save form. Questions with missing merge tag: ${questions}`,
                        });
                        return;
                    }

                    if (!this.formData.elements || this.formData.elements.length === 0) {
                        this.errorToast({ text: 'Form must have at least one question.' });
                        return;
                    }

                    // need to wait until formData make updates
                    const formData = JSON.parse(JSON.stringify(this.formData));
                    delete formData.elements;
                    const elements =
                        this.type === 'INVOICE_VARIABLES'
                            ? this.formData.elements.map((item) => {
                                  return {
                                      ...item,
                                      mergeTagDescription:
                                          item.mergeTagDescription || item.mergeTag?.split('_').join(' ').toLowerCase(),
                                      showOnBooking: item.showOnBooking !== undefined ? item.showOnBooking : true,
                                  };
                              })
                            : this.formData.elements;
                    let formJson = {
                        elements,
                        ...formData,
                        showPageNavigator: false,
                    };
                    const body = {
                        form_json: formJson,
                        title: this.title,
                        variable: {
                            name: this.customVariable,
                            title: this.customVariableName,
                        },
                        type: this.type,
                        properties: this.selectedProperties.map((item) => item.property_id),
                        is_enabled: this.enabled,
                        show_on_booking: this.show_on_booking,
                    };
                    if (this.type === 'HOUSEKEEPER') {
                        delete body.variable;
                    }
                    if (this.formId) {
                        await api.patch(HOSTED_FORM_ENDPOINT(this.formId), body);
                    } else {
                        const { data } = await api.post(HOSTED_FORM_ENDPOINT(), body);
                        this.$router.push(`/app/hosted-form/${data}/edit/`);
                    }
                    if (showSuccess) {
                        this.successToast({ text: 'Form was successfully saved.' });
                    }
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not save form' });
                } finally {
                    this.handleBusy(false);
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    .save-button {
        max-width: 150px;
    }

    .toolbar-row {
        gap: 15px;
        align-items: baseline;
    }

    .housekeeping-switch {
        margin-bottom: 10px !important;
        width: 100%;
    }

    .property-select {
        min-width: 200px;
    }

    .more-filters {
        width: fit-content !important;
    }

    .dropdown-content {
        @include media-breakpoint-down(sm) {
            max-width: 300px;
        }
    }

    .disabled {
        opacity: 0.7;
    }

    .toolbar-wrapper {
        @include media-breakpoint-down(md) {
            display: block;
            position: sticky;
            top: 0;
            left: 0;
            z-index: 100;
            background: #fff;
        }
    }
</style>
